import React, { useState } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────
export interface CalendarDayData {
  /** YYYY-MM-DD */
  date: string;
  status: 'non_compliant' | 'upcoming' | 'approval_pending' | 'rejected' | null;
  /** Total activities on this date across all 4 statuses */
  total?: number;
}

export interface CalendarScheduleItem {
  id: string;
  title: string;
  status: 'upcoming' | 'under_review' | 'approval_pending' | 'non_compliant' | 'rejected';
}

interface CalendarPanelProps {
  onClose: () => void;
  /** Called when user clicks a date cell; receives "YYYY-MM-DD" */
  onDateSelect: (date: string) => void;
  /** Currently selected date "YYYY-MM-DD" — highlighted in the grid */
  selectedDate: string;
  /** Activities for the selected date (API caps at 10) */
  scheduleItems: CalendarScheduleItem[];
  /** True total on the selected date — may exceed scheduleItems.length */
  totalCount: number;
  /** Per-day status colouring from the month API */
  dayData?: CalendarDayData[];
  loadingSchedule?: boolean;
  /** Called whenever the user navigates to a different month */
  onMonthChange?: (year: number, month: number) => void;
  /** Called when "View More" is clicked — navigation logic wired later */
  onViewMore?: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS = [
  'January','February','March','April','May',
  'June','July','August','September','October','November','December',
];
const DAY_LABELS = ['S','M','T','W','T','F','S'];
const DAY_NAMES  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const CUBE_COLORS: Record<string, string> = {
  non_compliant:    '#ec221f',
  upcoming:         '#e5a000',
  approval_pending: '#c00f0c',
  rejected:         '#975102',
};

const BADGE_META: Record<string, { label: string; color: string; bg: string }> = {
  upcoming:         { label: 'Upcoming',         color: '#e5a000', bg: 'rgba(229,160,0,0.10)'  },
  under_review:     { label: 'Under Review',     color: '#e5a000', bg: 'rgba(229,160,0,0.10)'  },
  approval_pending: { label: 'Approval Pending', color: '#c00f0c', bg: 'rgba(192,15,12,0.05)'  },
  non_compliant:    { label: 'Non Compliant',    color: '#ec221f', bg: 'rgba(192,15,12,0.10)'  },
  rejected:         { label: 'Rejected',         color: '#975102', bg: 'rgba(151,81,2,0.10)'   },
};

const BORDER_COLORS: Record<string, string> = {
  upcoming:         '#e5a000',
  under_review:     '#e5a000',
  approval_pending: '#c00f0c',
  non_compliant:    '#ec221f',
  rejected:         '#975102',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildGrid(year: number, month: number): (number | null)[][] {
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const rows: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}

function ordinal(n: number): string {
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** Returns "YYYY-MM-DD" for a local date, without timezone shift. */
function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Today Mon 7th Aug" or "Mon 7th Aug" for non-today dates */
function scheduleLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const prefix = dateStr === toYMD(new Date()) ? 'Today ' : '';
  return `${prefix}${DAY_NAMES[d.getDay()]} ${ordinal(d.getDate())} ${MONTHS[d.getMonth()].slice(0, 3)}`;
}

/** "Aug 7, 2026" — used in tooltip */
function tooltipDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function ComplianceDashboardCalendar({
  onClose,
  onDateSelect,
  selectedDate,
  scheduleItems,
  totalCount,
  dayData         = [],
  loadingSchedule = false,
  onMonthChange,
  onViewMore,
}: CalendarPanelProps) {
  const today = new Date();
  const todayStr = toYMD(today);

  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const yr    = view.getFullYear();
  const mo    = view.getMonth();
  const weeks = buildGrid(yr, mo);

  // Hover tooltip state
  const [hoveredCell, setHoveredCell] = useState<{
    label: string; total: number; x: number; y: number;
  } | null>(null);

  // Build date → status & total lookups for coloured cubes + tooltip
  const dayMap:      Record<string, string> = {};
  const dayTotalMap: Record<string, number> = {};
  dayData.forEach((d) => {
    if (d.status) dayMap[d.date] = d.status;
    if (d.total)  dayTotalMap[d.date] = d.total;
  });

  function cubeColor(day: number): string {
    const key = cellKey(day);
    return CUBE_COLORS[dayMap[key]] || '#e8ecf0';
  }

  function cellKey(day: number): string {
    return `${yr}-${String(mo + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const extraCount = totalCount > scheduleItems.length ? totalCount - scheduleItems.length : 0;

  return (
    <div className="cd2-cal-panel">
      {/* ── Header ── */}
      <div className="cd2-cal-header">
        <div className="cd2-cal-nav">
          <button
            className="cd2-cal-nav-btn"
            title="Previous month"
            onClick={() => {
              const prev = new Date(yr, mo - 1, 1);
              setView(prev);
              if (onMonthChange) onMonthChange(prev.getFullYear(), prev.getMonth() + 1);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8l4-4" stroke="#1e1e1e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="cd2-cal-month-title">{MONTHS[mo]}, {yr}</span>
          <button
            className="cd2-cal-nav-btn"
            title="Next month"
            onClick={() => {
              const next = new Date(yr, mo + 1, 1);
              setView(next);
              if (onMonthChange) onMonthChange(next.getFullYear(), next.getMonth() + 1);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="#1e1e1e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <button className="cd2-cal-close-btn" onClick={onClose} title="Close calendar">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="#666" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* ── Calendar grid ── */}
      <div className="cd2-cal-grid">
        {/* Day-of-week headers */}
        <div className="cd2-cal-week-row">
          <div className="cd2-cal-wk-num" />
          {DAY_LABELS.map((d, i) => (
            <div className="cd2-cal-day-hdr" key={i}>{d}</div>
          ))}
        </div>

        {weeks.map((week, wi) => (
          <div className="cd2-cal-week-row" key={wi}>
            <div className="cd2-cal-wk-num">W{wi + 1}</div>
            {week.map((day, di) => {
              if (day === null) {
                return <div className="cd2-cal-cell" key={di} />;
              }
              const key        = cellKey(day);
              const isToday    = key === todayStr;
              const isSelected = key === selectedDate;
              const cellTotal  = dayTotalMap[key];

              let cellClass = 'cd2-cal-cell cd2-cal-cell--clickable';
              if (isToday)         cellClass += ' cd2-cal-cell--today';
              else if (isSelected) cellClass += ' cd2-cal-cell--selected';

              return (
                <div
                  className={cellClass}
                  key={di}
                  onClick={() => onDateSelect(key)}
                  onMouseEnter={cellTotal ? (e) => {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setHoveredCell({
                      label: tooltipDateLabel(key),
                      total: cellTotal,
                      x: rect.left + rect.width / 2,
                      y: rect.top - 8,
                    });
                  } : undefined}
                  onMouseLeave={cellTotal ? () => setHoveredCell(null) : undefined}
                >
                  {/* Only show colour cube for plain (non-highlighted) cells */}
                  {!isToday && !isSelected && (
                    <div className="cd2-cal-cube" style={{ background: cubeColor(day) }} />
                  )}
                  <span className={`cd2-cal-day-lbl${isToday || isSelected ? ' is-highlighted' : ''}`}>
                    {day}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── Legend ── */}
      <div className="cd2-cal-legend">
        <div className="cd2-cal-legend-divider" />
        <div className="cd2-cal-legend-items">
          {[
            { label: 'Non Compliant',    color: '#ec221f' },
            { label: 'Upcoming',         color: '#e5a000' },
            { label: 'Approval Pending', color: '#c00f0c' },
            { label: 'Rejected',         color: '#975102' },
          ].map(({ label, color }) => (
            <div className="cd2-cal-legend-item" key={label}>
              <span className="cd2-cal-legend-dot" style={{ background: color }} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Hover tooltip (fixed, follows cell position) ── */}
      {hoveredCell && (
        <div
          className="cd2-cal-date-tooltip"
          style={{ left: hoveredCell.x, top: hoveredCell.y }}
        >
          <span className="cd2-cal-tooltip-date">{hoveredCell.label}</span>
          <span className="cd2-cal-tooltip-count">
            {hoveredCell.total} {hoveredCell.total === 1 ? 'activity' : 'activities'}
          </span>
        </div>
      )}

      {/* ── Schedule section header ── */}
      <div className="cd2-cal-sched-header">
        <div className="cd2-cal-sched-divider" />
        <div className="cd2-cal-sched-header-row">
          <span className="cd2-cal-sched-title">{scheduleLabel(selectedDate)}</span>
          {totalCount > 0 && (
            <button className="cd2-cal-sched-total-btn" onClick={onViewMore} title="Open in list view">
              {totalCount} {totalCount === 1 ? 'activity' : 'activities'}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                <path d="M2.5 6h7M6.5 3l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Schedule items — only this section scrolls ── */}
      <div className="cd2-cal-schedule-list">
        {loadingSchedule ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div className="cd2-cal-sched-item cd2-cal-sched-skeleton" key={i}>
              <span className="cd2-skeleton" style={{ width: '65%', height: 11, borderRadius: 4, display: 'inline-block' }} />
              <span className="cd2-skeleton" style={{ width: 64,  height: 22, borderRadius: 20, display: 'inline-block' }} />
            </div>
          ))
        ) : scheduleItems.length === 0 ? (
          <div className="cd2-cal-empty">No activities due on this date</div>
        ) : (
          <>
            {scheduleItems.map((item) => {
              const badge     = BADGE_META[item.status] || BADGE_META.upcoming;
              const borderClr = BORDER_COLORS[item.status] || '#e5a000';
              return (
                <div className="cd2-cal-sched-item" key={item.id} style={{ borderLeftColor: borderClr }}>
                  <span className="cd2-cal-sched-name">{item.title}</span>
                  <span className="cd2-cal-badge" style={{ color: badge.color, background: badge.bg }}>
                    {badge.label}
                  </span>
                </div>
              );
            })}

            {/* Show how many more exist beyond the 10-item cap */}
            {extraCount > 0 && (
              <div className="cd2-cal-more-note">
                +{extraCount} more {extraCount === 1 ? 'activity' : 'activities'} on this date
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Open in list view — fixed below the scroll area ── */}
      {scheduleItems.length > 0 && (
        <button className="cd2-cal-view-more-btn" onClick={onViewMore}>
          Open in list view
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
            <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
